import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
    Text, Box, IconButton, Tooltip, Spinner, Stack, HStack,
    Modal, ModalOverlay, ModalContent, ModalBody, ModalFooter, Button,
    Icon, useBreakpointValue
} from "@chakra-ui/react";
import { MdRefresh, MdPeopleAlt } from "react-icons/md";
import AdminMemberRow from "./AdminMemberRow";
import { useAdminPalette } from "./adminPalette";

/**
 * Manages the admin members list with CRUD operations and confirmation dialog.
 * @param {Object} props
 * @param {number} props.userConnectedRight - Connected user's admin right level (1=admin, 2=super admin)
 */
function AdminMembers({ userConnectedRight }) {
    const [allAdmin, setAllAdmin] = useState([]);
    const [loading, setLoading] = useState(false);
    const [smShow, setSmShow] = useState(false);
    const [adminToRemove, setAdminToRemove] = useState({});
    const isMobile = useBreakpointValue({ base: true, sm: false });

    /**
     * Fetches all admin users from API.
     */
    const checkAllUserAdmin = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get('/admin/users/all');
            setAllAdmin(response.data);
        } catch (error) {
            console.error("Failed to fetch admin users:", error);
            toast.error("Erreur lors du chargement des admins");
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Removes admin rights from the selected user.
     */
    const removeAdmin = async () => {
        setLoading(true);
        try {
            await axios.put("/admin/users/switch_right/" + adminToRemove.id.toString() + "/0");
            await checkAllUserAdmin();
            setSmShow(false);
            toast.success("L'utilisateur n'est plus admin");
        } catch (error) {
            console.error("Failed to remove admin:", error);
            toast.error("Erreur lors de la suppression");
        } finally {
            setLoading(false);
        }
    };

    /**
     * Opens the confirmation dialog for removing an admin.
     * @param {Object} value - Admin user to remove
     */
    const alertRemoveAdmin = (value) => {
        setAdminToRemove(value);
        setSmShow(true);
    };

    useEffect(() => {
        checkAllUserAdmin();
    }, [checkAllUserAdmin]);

    return (
        <Box>
            <RemoveAdminDialog smShow={smShow} setSmShow={setSmShow} adminName={adminToRemove.name} loading={loading} onConfirm={removeAdmin} />
            <AdminListHeader isMobile={isMobile} loading={loading} onRefresh={checkAllUserAdmin} />
            <AdminList allAdmin={allAdmin} loading={loading} isMobile={isMobile} userConnectedRight={userConnectedRight} onRemove={alertRemoveAdmin} />
        </Box>
    );
}

/**
 * Confirmation dialog for removing an admin user.
 * @param {Object} props
 * @param {boolean} props.smShow - Dialog open state
 * @param {Function} props.setSmShow - Dialog setter
 * @param {string} props.adminName - Name of admin to remove
 * @param {boolean} props.loading - Loading state
 * @param {Function} props.onConfirm - Confirm removal handler
 */
function RemoveAdminDialog({ smShow, setSmShow, adminName, loading, onConfirm }) {
    return (
        <Modal isOpen={smShow} onClose={() => setSmShow(false)} isCentered>
            <ModalOverlay />
            <ModalContent borderRadius="xl">
                <ModalBody pt={6} textAlign="center">
                    <Text fontSize="1.05rem">
                        Enlever <b>{adminName}</b> de sa fonction d'admin ?
                    </Text>
                </ModalBody>
                <ModalFooter justifyContent="center" pb={6} gap={2}>
                    <Button variant="ghost" onClick={() => setSmShow(false)} isDisabled={loading}>
                        Non
                    </Button>
                    <Button colorScheme="red" onClick={onConfirm} isDisabled={loading}>
                        {loading ? <Spinner size="sm" /> : "Oui"}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

/**
 * Renders the header icon box for the admin list.
 * @param {Object} props
 * @param {boolean} props.isMobile - Mobile viewport flag
 */
function HeaderIconBox({ isMobile }) {
    const palette = useAdminPalette();
    return (
        <Box display="flex" alignItems="center" justifyContent="center"
            w={isMobile ? "30px" : "34px"} h={isMobile ? "30px" : "34px"} borderRadius="8px"
            background={palette.accentTint}>
            <Icon as={MdPeopleAlt} color={palette.accent} boxSize={isMobile ? "16px" : "18px"} />
        </Box>
    );
}

/**
 * Header section with title and refresh button for the admin list.
 * @param {Object} props
 * @param {boolean} props.isMobile - Mobile flag
 * @param {boolean} props.loading - Loading state
 * @param {Function} props.onRefresh - Refresh handler
 */
function AdminListHeader({ isMobile, loading, onRefresh }) {
    const palette = useAdminPalette();
    return (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4} pb={3}
            borderBottom="1px solid" borderColor={palette.border}>
            <HStack spacing={2} alignItems="center">
                <HeaderIconBox isMobile={isMobile} />
                <Text color={palette.textPrimary} fontWeight={700}
                    fontSize={isMobile ? "0.88rem" : "0.95rem"}>
                    Équipe Admin
                </Text>
            </HStack>
            <Tooltip label="Actualiser" hasArrow placement="top">
                <IconButton aria-label="Actualiser" size="sm" variant="ghost" onClick={onRefresh} isDisabled={loading}
                    minW="auto" h="auto" p={isMobile ? "4px" : "6px"} color={palette.textSecondary}
                    _hover={{ background: palette.rowBg, color: palette.accent }}
                    icon={loading
                        ? <Spinner size="xs" color={palette.accent} />
                        : <Icon as={MdRefresh} boxSize={isMobile ? "14px" : "16px"} />} />
            </Tooltip>
        </Box>
    );
}

/**
 * Renders the scrollable list of admin members with loading and empty states.
 * @param {Object} props
 * @param {Array} props.allAdmin - List of admin users
 * @param {boolean} props.loading - Loading state
 * @param {boolean} props.isMobile - Mobile flag
 * @param {number} props.userConnectedRight - Connected user's right level
 * @param {Function} props.onRemove - Remove admin handler
 */
function AdminList({ allAdmin, loading, isMobile, userConnectedRight, onRemove }) {
    const palette = useAdminPalette();
    if (loading && allAdmin.length === 0) {
        return <Box display="flex" justifyContent="center" py={8}><Spinner color={palette.accent} /></Box>;
    }

    if (allAdmin.length === 0) {
        return <Text color={palette.textMuted} textAlign="center" py={6} fontSize="0.85rem">Aucun administrateur</Text>;
    }

    return (
        <Box maxH="400px" overflowY="auto">
            <Stack spacing={2}>
                {allAdmin.map((value, index) => (
                    <AdminMemberRow key={index} admin={value} isMobile={isMobile} userConnectedRight={userConnectedRight} loading={loading} onRemove={onRemove} />
                ))}
            </Stack>
        </Box>
    );
}

export default AdminMembers;
